import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, Lock, Mail, KeyRound, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { LOGO_URL } from "../lib/api";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: code+new password
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      toast.success("Code gesendet (falls Konto existiert)");
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Versand");
    } finally {
      setBusy(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (!code || code.length !== 6) return toast.error("Bitte 6-stelligen Code eingeben");
    if (!password || password.length < 6) return toast.error("Passwort braucht mindestens 6 Zeichen");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", {
        email: email.trim().toLowerCase(),
        code: code.trim(),
        new_password: password,
      });
      toast.success("Passwort erfolgreich geändert");
      navigate("/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Code ungültig oder abgelaufen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 hero-bg">
      <div className="w-full max-w-md" data-testid="forgot-page">
        <div className="flex justify-center mb-6">
          <img src={LOGO_URL} alt="TSU Partners" className="h-24 w-24 object-contain" />
        </div>
        <div className="bg-white backdrop-blur-xl border border-slate-200 rounded-2xl p-8 shadow-[0_20px_80px_rgba(0,102,255,0.15)]">
          <button
            onClick={() => navigate("/login")}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#1D5BFF] mb-4"
            data-testid="back-to-login"
          >
            <ArrowLeft className="w-3 h-3" /> Zurück zum Login
          </button>
          <h1 className="font-display text-2xl sm:text-3xl font-black text-slate-900 tracking-tighter mb-1">
            Passwort vergessen?
          </h1>
          <p className="text-sm text-slate-600 mb-6">
            {step === 1
              ? "Gib deine E-Mail an. Wir senden dir einen 6-stelligen Code zum Zurücksetzen."
              : "Prüfe dein Postfach. Gib den Code und ein neues Passwort ein."}
          </p>

          {step === 1 && (
            <form onSubmit={requestCode} className="space-y-4">
              <label className="block">
                <span className="text-xs text-slate-600 uppercase tracking-wider">E-Mail</span>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email-input"
                    className="w-full bg-[#F5F8FD] border border-slate-200 rounded-xl pl-9 pr-3 py-2.5 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#1D5BFF] focus:ring-2 focus:ring-[#1D5BFF]/20 transition-all"
                    placeholder="name@beispiel.de"
                  />
                </div>
              </label>
              <button
                type="submit"
                disabled={busy}
                data-testid="forgot-submit-btn"
                className="btn-brand w-full font-bold py-3 rounded-full flex items-center justify-center gap-2"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Code senden <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={doReset} className="space-y-4">
              <label className="block">
                <span className="text-xs text-slate-600 uppercase tracking-wider">6-stelliger Code</span>
                <div className="relative mt-1">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    data-testid="reset-code-input"
                    className="w-full bg-[#F5F8FD] border border-slate-200 rounded-xl pl-9 pr-3 py-2.5 text-slate-900 placeholder:text-slate-400 tracking-[0.5em] text-center font-mono text-lg focus:outline-none focus:border-[#1D5BFF] focus:ring-2 focus:ring-[#1D5BFF]/20"
                    placeholder="123456"
                  />
                </div>
              </label>
              <label className="block">
                <span className="text-xs text-slate-600 uppercase tracking-wider">Neues Passwort</span>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="password"
                    required
                    minLength={6}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-password-input"
                    className="w-full bg-[#F5F8FD] border border-slate-200 rounded-xl pl-9 pr-3 py-2.5 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#1D5BFF] focus:ring-2 focus:ring-[#1D5BFF]/20"
                    placeholder="Mindestens 6 Zeichen"
                  />
                </div>
              </label>
              <button
                type="submit"
                disabled={busy}
                data-testid="reset-submit-btn"
                className="btn-brand w-full font-bold py-3 rounded-full flex items-center justify-center gap-2"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Passwort setzen <ArrowRight className="w-4 h-4" /></>}
              </button>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-xs text-slate-500 hover:text-[#1D5BFF] block mx-auto"
                data-testid="resend-code-link"
              >
                Keinen Code erhalten? Nochmal senden
              </button>
            </form>
          )}

          <div className="mt-6 text-center text-sm text-slate-600">
            <Link to="/login" className="text-[#4DBFFF] hover:text-[#1D5BFF] font-semibold">
              Zurück zum Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
